/**
 * Packages API — client functions for the searcher form.
 *
 * Same token/auth pattern as the hotels feature: a tenant token resolved from
 * the hostname, never persisted here.
 */

import { getTenantApiToken } from '../../../../core/auth/getTenantApiToken';
import {
  packageAvailableDatesUrl,
  packageDestinationsUrl,
  packageOriginsUrl,
} from './packages.endpoints';
import type {
  PackageAvailableDatesResponse,
  PackagePlace,
  PackagePlaceResponse,
} from './packages.types';
import type { AvailableDates } from '../../shared/availableMonths';

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const token = await getTenantApiToken();

  if (!token) {
    throw new Error('packages.api.missingAuthToken');
  }

  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`packages.api.requestFailed:${response.status}`);
  }

  return (await response.json()) as T;
}

/**
 * Merges provider rows that name the same city into a single option, keeping
 * every id so the results route receives the full `id1,id2` list.
 */
export function mergePlacesByName(rows: PackagePlaceResponse[] | null | undefined): PackagePlace[] {
  if (!Array.isArray(rows)) return [];

  const byName = new Map<string, PackagePlace>();

  rows.forEach((row) => {
    const name = String(row?.name ?? '').trim();
    if (!name || row?.id == null) return;

    const key = name.toLowerCase();
    const existing = byName.get(key);

    if (existing) {
      if (!existing.ids.includes(row.id)) existing.ids.push(row.id);
      return;
    }

    byName.set(key, { name, ids: [row.id] });
  });

  return [...byName.values()];
}

export async function fetchPackageOrigins(signal?: AbortSignal): Promise<PackagePlace[]> {
  const rows = await getJson<PackagePlaceResponse[]>(packageOriginsUrl(), signal);
  return mergePlacesByName(rows);
}

export async function fetchPackageDestinations(
  originId: string,
  signal?: AbortSignal,
): Promise<PackagePlace[]> {
  const rows = await getJson<PackagePlaceResponse[]>(packageDestinationsUrl(originId), signal);
  return mergePlacesByName(rows);
}

export async function fetchPackageAvailableDates(
  originId: string,
  destinationId: string,
  signal?: AbortSignal,
): Promise<AvailableDates | null> {
  const body = await getJson<PackageAvailableDatesResponse>(
    packageAvailableDatesUrl(originId, destinationId),
    signal,
  );
  return body?.availableDates ?? null;
}
